import React, { useCallback, useMemo } from "react";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import { useAccounts } from "@hooks/useAccounts";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { FilterPickerList } from "@components/pageHeader/filters/FilterPickerList/FilterPickerList";
import { BaseFilterProps } from "../../lib";

interface Props extends BaseFilterProps {
    hardcodedEntityId?: number;
    availableClassifications?: AccountClass[];
}

export const AccountCodesFilter: React.FC<Props> = ({
    filters,
    onChange,
    hardcodedEntityId,
    availableClassifications,
}) => {
    const { accounts } = useAccounts({
        entityId: hardcodedEntityId,
        classes: availableClassifications,
    });

    const items = useMemo(
        () =>
            accounts.map((account) => ({
                value: account.code,
                label: account.name,
            })),
        [accounts],
    );
    const codeTextByCode = useMemo(
        () =>
            new Map(
                accounts.map((account) => [account.code, account.codeText]),
            ),
        [accounts],
    );

    const matchesNameOrCode = useCallback(
        (item: PickerItemData<number>, query: string) => {
            const lowerQuery = query.toLowerCase();
            return (
                item.label.toLowerCase().includes(lowerQuery) ||
                (codeTextByCode.get(item.value) ?? "")
                    .toLowerCase()
                    .includes(lowerQuery)
            );
        },
        [codeTextByCode],
    );

    const reverseSelection = useCallback(() => {
        onChange({
            accountCodes: accounts
                .filter((item) => !filters.accountCodes?.includes(item.code))
                .map(({ code }) => code),
        });
    }, [accounts, onChange, filters.accountCodes]);

    return (
        <FilterPickerList
            items={items}
            value={filters.accountCodes ?? []}
            onValueChange={(accountCodes) => onChange({ accountCodes })}
            filter={matchesNameOrCode}
            emptyLabel="No account codes found"
            onDeselect={() => onChange({ accountCodes: undefined })}
            onReverse={reverseSelection}
        />
    );
};
